import React, { useState, useEffect, useCallback, useRef } from "react";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import { useNavigate, Link } from "react-router-dom";
import {
  FiArrowLeft,
  FiChevronRight,
  FiSearch,
  FiRefreshCw,
  FiCalendar,
  FiExternalLink,
  FiX,
  FiLayers,
} from "react-icons/fi";
import { getGlobalCoinHistory } from "../api/userCoinHistoryApi";

const SOURCE_TABS = [
  { key: "all", label: "All Sources", icon: "🌐" },
  { key: "mining", label: "App Mining", icon: "⚡" },
  { key: "browser", label: "Zeta Browser", icon: "💻" },
  { key: "event", label: "Event Prizes", icon: "🏆" },
  { key: "event_points", label: "Event Points", icon: "🎯" },
  { key: "mini_games", label: "Mini Games", icon: "🎮" },
  { key: "ban", label: "Reversions", icon: "🛡️" },
];

const TYPE_OPTIONS = [
  { key: "all", label: "All Types" },
  { key: "CREDIT", label: "+ Credits Only" },
  { key: "DEBIT", label: "- Debits Only" },
];

const getSourceBadgeStyle = (source) => {
  switch (source) {
    case "app_mining":
    case "mining":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "zeta_browser":
    case "browser":
      return "bg-blue-50 text-blue-700 border-blue-200";
    case "event_prize":
    case "event":
      return "bg-amber-50 text-amber-800 border-amber-200";
    case "event_point":
    case "event_points":
    case "points":
      return "bg-purple-50 text-purple-700 border-purple-200";
    case "game":
    case "mini_games":
    case "boomcards":
    case "boom_cards":
    case "tap_game":
    case "tap":
    case "slots":
    case "slots_game":
    case "spinwheel":
    case "spin_wheel":
    case "spin":
      return "bg-indigo-50 text-indigo-700 border-indigo-200";
    case "game_2048":
    case "2048":
      return "bg-amber-50 text-amber-800 border-amber-200";
    case "fruit_bomb":
    case "fruit-bomb":
    case "fruit_Bomb":
      return "bg-rose-50 text-rose-700 border-rose-200";
    case "admin_reversion":
    case "reversion":
    case "reversions":
    case "point_reversion":
    case "event_reversion":
    case "ban":
      return "bg-rose-50 text-rose-700 border-rose-200";
    default:
      return "bg-gray-100 text-gray-700 border-gray-200";
  }
};

const getSourceIcon = (source) => {
  switch (source) {
    case "app_mining":
    case "mining":
      return "⚡";
    case "zeta_browser":
    case "browser":
      return "💻";
    case "event_prize":
    case "event":
      return "🏆";
    case "event_point":
    case "event_points":
    case "points":
      return "🎯";
    case "game":
    case "mini_games":
    case "boomcards":
    case "boom_cards":
    case "tap_game":
    case "tap":
      return "🎮";
    case "game_2048":
    case "2048":
      return "🧩";
    case "fruit_bomb":
    case "fruit-bomb":
    case "fruit_Bomb":
      return "🍉";
    case "admin_reversion":
    case "reversion":
    case "point_reversion":
    case "event_reversion":
    case "ban":
      return "🛡️";
    default:
      return "🪙";
  }
};

const formatCoinAmount = (amt) => {
  if (amt == null) return "0.0000";
  const num = Number(amt);
  return isNaN(num) ? String(amt) : num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  } catch {
    return String(dateStr);
  }
};

export default function CoinHistory() {
  const navigate = useNavigate();

  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1, page: 1, perPage: 20 });
  const [summary, setSummary] = useState({ currentPageCredited: 0, currentPageDebited: 0 });
  const [sourceTotals, setSourceTotals] = useState({
    appMining: 0,
    zetaBrowser: 0,
    eventPrizes: 0,
    eventPoints: 0,
  });

  const debounceTimer = useRef(null);

  const computeSourceTotalsFromList = (list) => {
    let appMining = 0;
    let eventPrizes = 0;
    let eventPoints = 0;
    const browserTxs = [];

    (list || []).forEach((tx) => {
      const src = String(tx.source || "").toLowerCase().trim();
      const isDebit = String(tx.transactionType || "").toUpperCase() === "DEBIT";
      const amt = Number(tx.amount) || 0;
      const signedAmt = isDebit ? -amt : amt;

      if (src === "app_mining" || src === "mining") {
        appMining += signedAmt;
      } else if (src === "zeta_browser" || src === "browser") {
        browserTxs.push(tx);
      } else if (src === "event_prize" || src === "event_prizes" || src === "event") {
        eventPrizes += signedAmt;
      } else if (src === "event_point" || src === "event_points" || src === "points") {
        eventPoints += signedAmt;
      }
    });

    let zetaBrowser = 0;
    if (browserTxs.length > 0) {
      const sortedBrowserTxs = [...browserTxs].sort((a, b) => {
        const dateA = new Date(a.transactionDate || a.createdAt || 0).getTime();
        const dateB = new Date(b.transactionDate || b.createdAt || 0).getTime();
        return dateB - dateA;
      });

      const latestBrowserTx = sortedBrowserTxs[0];
      if (
        latestBrowserTx &&
        latestBrowserTx.balanceAfter != null &&
        !isNaN(Number(latestBrowserTx.balanceAfter))
      ) {
        zetaBrowser = Number(latestBrowserTx.balanceAfter);
      } else {
        zetaBrowser = browserTxs.reduce((sum, tx) => {
          const isDebit = String(tx.transactionType || "").toUpperCase() === "DEBIT";
          const amt = Number(tx.amount) || 0;
          return sum + (isDebit ? -amt : amt);
        }, 0);
      }
    }

    return {
      appMining: Math.max(0, appMining),
      zetaBrowser: Math.max(0, zetaBrowser),
      eventPrizes: Math.max(0, eventPrizes),
      eventPoints: Math.max(0, eventPoints),
    };
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    setPage(1);
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => setDebouncedSearch(value), 400);
  };

  const fetchLedger = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError("");

    try {
      let apiSource = sourceFilter;
      if (sourceFilter === "mini_games" || sourceFilter === "game" || sourceFilter === "games") apiSource = "mini_games";
      else if (sourceFilter === "event_points" || sourceFilter === "points") apiSource = "event_points";

      const res = await getGlobalCoinHistory({
        search: debouncedSearch,
        source: apiSource,
        type: typeFilter,
        page,
        perPage,
      });

      if (res?.status && res?.data) {
        const rawHistory = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.history)
          ? res.data.history
          : [];
        let filteredList = rawHistory;
        if (sourceFilter && sourceFilter !== "all") {
          filteredList = filteredList.filter((tx) => {
            const s = String(tx.source || "").toLowerCase().trim();
            if (sourceFilter === "mining") return s === "app_mining" || s === "mining";
            if (sourceFilter === "browser") return s === "zeta_browser" || s === "browser";
            if (sourceFilter === "event") return s === "event_prize" || s === "event_prizes" || s === "event";
            if (sourceFilter === "points") return s === "event_point" || s === "event_points" || s === "points";
            if (sourceFilter === "ban") {
              const isDebit = String(tx.transactionType || tx.type || "").toUpperCase() === "DEBIT";
              return (
                s === "admin_reversion" ||
                s === "ban" ||
                s === "reversion" ||
                s === "reversions" ||
                s === "event_reversion" ||
                s === "point_reversion" ||
                s.includes("revert") ||
                isDebit
              );
            }
            return true;
          });
        }
        if (typeFilter && typeFilter !== "all") {
          filteredList = filteredList.filter(
            (tx) => String(tx.transactionType || tx.type || "").toUpperCase() === typeFilter.toUpperCase()
          );
        }


        setHistory(filteredList);

        const computedSummary = res.data?.summary || {
          currentPageCredited: filteredList
            .filter((h) => (h.transactionType || "").toUpperCase() === "CREDIT")
            .reduce((acc, h) => acc + (Number(h.amount) || 0), 0),
          currentPageDebited: filteredList
            .filter((h) => (h.transactionType || "").toUpperCase() === "DEBIT")
            .reduce((acc, h) => acc + (Number(h.amount) || 0), 0),
        };
        setSummary(computedSummary);

        if (
          computedSummary.appMining != null ||
          computedSummary.app_mining != null ||
          computedSummary.zetaBrowser != null ||
          computedSummary.zeta_browser != null ||
          computedSummary.eventPrizes != null ||
          computedSummary.event_prize != null ||
          computedSummary.eventPoints != null ||
          computedSummary.event_point != null
        ) {
          setSourceTotals({
            appMining: Number(computedSummary.appMining ?? computedSummary.app_mining ?? computedSummary.totalAppMining ?? computedSummary.miningTotal ?? 0),
            zetaBrowser: Number(computedSummary.zetaBrowser ?? computedSummary.zeta_browser ?? computedSummary.totalZetaBrowser ?? computedSummary.browserTotal ?? 0),
            eventPrizes: Number(computedSummary.eventPrizes ?? computedSummary.event_prize ?? computedSummary.totalEventPrizes ?? computedSummary.eventPrizeTotal ?? 0),
            eventPoints: Number(computedSummary.eventPoints ?? computedSummary.event_point ?? computedSummary.event_points ?? computedSummary.totalEventPoints ?? computedSummary.eventPointsTotal ?? 0),
          });
        } else {
          setSourceTotals(computeSourceTotalsFromList(rawHistory));
        }

        const serverPagination = res.data?.pagination || res.pagination || {};
        const totalRecords =
          typeof serverPagination?.total === "number"
            ? serverPagination.total
            : typeof res.data?.total === "number"
            ? res.data.total
            : typeof res.total === "number"
            ? res.total
            : filteredList.length;

        const totalPagesCount =
          typeof serverPagination?.totalPages === "number"
            ? serverPagination.totalPages
            : typeof serverPagination?.lastPage === "number"
            ? serverPagination.lastPage
            : Math.max(1, Math.ceil(totalRecords / perPage));

        const currentPageNum =
          typeof serverPagination?.page === "number"
            ? serverPagination.page
            : typeof serverPagination?.currentPage === "number"
            ? serverPagination.currentPage
            : page;

        setPagination({
          total: totalRecords,
          totalPages: totalPagesCount,
          page: currentPageNum,
          perPage,
        });
      } else {
        throw new Error(res?.message || "Failed to load coin history ledger");
      }
    } catch (err) {
      console.error("Error loading global coin history:", err);
      setError(err.message || "Failed to load coin history ledger");
      setHistory([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [debouncedSearch, sourceFilter, typeFilter, page, perPage]);

  useEffect(() => {
    fetchLedger(false);
  }, [fetchLedger]);

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />

        <div className="p-5 sm:p-8 space-y-6 w-full">
          {/* Breadcrumbs & Header Title */}
          <div className="space-y-3">
            <div className="flex items-center flex-wrap gap-1.5 text-xs text-gray-500 font-medium">
              <Link
                to="/user"
                className="hover:text-black hover:underline transition flex items-center gap-1"
              >
                Users
              </Link>
              <FiChevronRight size={12} className="text-gray-400 flex-shrink-0" />
              <span className="text-gray-900 font-bold bg-[#ffffcc] px-2 py-0.5 rounded">
                Coin History Ledger
              </span>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    if (window.history.length > 1) {
                      navigate(-1);
                    } else {
                      navigate("/user");
                    }
                  }}
                  className="p-2.5 bg-white hover:bg-gray-50 border border-gray-200 rounded-xl text-gray-700 hover:text-black transition shadow-xs cursor-pointer flex items-center justify-center"
                  title="Back to Previous Page"
                  aria-label="Back"
                >
                  <FiArrowLeft size={18} />
                </button>
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="text-2xl font-black text-gray-900 tracking-tight">
                      Coin History Ledger
                    </h1>
                    <span className="bg-[#ffff00] text-black text-xs font-bold px-2.5 py-0.5 rounded-full shadow-2xs">
                      Network Audit
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Audited transaction ledger tracking App Mining, Zeta Browser, Event Rewards, and Reversions
                  </p>
                </div>
              </div>

              <button
                onClick={() => fetchLedger(true)}
                disabled={refreshing || loading}
                className="flex items-center gap-2 text-xs font-bold text-gray-800 bg-white hover:bg-gray-50 border border-gray-200 px-4 py-2.5 rounded-xl transition shadow-xs self-start sm:self-auto cursor-pointer"
              >
                <FiRefreshCw
                  size={14}
                  className={refreshing ? "animate-spin text-[#999900]" : ""}
                />
                <span>Refresh Ledger</span>
              </button>
            </div>
          </div>

          {/* Quick Metrics Bar: 4 Source Breakdown Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* 1. Total of App Mining */}
            <div className="bg-white rounded-2xl shadow-sm border border-emerald-100 p-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-emerald-700 font-semibold uppercase tracking-wider flex items-center gap-1">
                  ⚡ Total of App Mining
                </p>
                <p className="text-2xl font-black text-emerald-600 font-mono mt-0.5">
                  {formatCoinAmount(sourceTotals.appMining)} ZX
                </p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center text-xl shadow-2xs">
                ⚡
              </div>
            </div>

            {/* 2. Zeta Browser Total */}
            <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-700 font-semibold uppercase tracking-wider flex items-center gap-1">
                  💻 Zeta Browser Total
                </p>
                <p className="text-2xl font-black text-blue-600 font-mono mt-0.5">
                  {formatCoinAmount(sourceTotals.zetaBrowser)} ZX
                </p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center text-xl shadow-2xs">
                💻
              </div>
            </div>

            {/* 3. Event Prizes */}
            <div className="bg-white rounded-2xl shadow-sm border border-amber-100 p-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-amber-800 font-semibold uppercase tracking-wider flex items-center gap-1">
                  🏆 Event Prizes
                </p>
                <p className="text-2xl font-black text-amber-600 font-mono mt-0.5">
                  {formatCoinAmount(sourceTotals.eventPrizes)} ZX
                </p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-800 flex items-center justify-center text-xl shadow-2xs">
                🏆
              </div>
            </div>

            {/* 4. Event Points */}
            <div className="bg-white rounded-2xl shadow-sm border border-purple-100 p-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-purple-700 font-semibold uppercase tracking-wider flex items-center gap-1">
                  🎯 Event Points
                </p>
                <p className="text-2xl font-black text-purple-600 font-mono mt-0.5">
                  {formatCoinAmount(sourceTotals.eventPoints)} PTS
                </p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-700 flex items-center justify-center text-xl shadow-2xs">
                🎯
              </div>
            </div>
          </div>

          {/* Search, Filter Toolbar */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 space-y-3">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
              {/* Search Bar */}
              <div className="relative flex-1">
                <FiSearch
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
                  size={15}
                />
                <input
                  type="text"
                  placeholder="Search by username, email, user ID, or details..."
                  value={search}
                  onChange={handleSearchChange}
                  className="w-full pl-9 pr-9 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#ffff00] focus:bg-white"
                />
                {search && (
                  <button
                    onClick={() => {
                      setSearch("");
                      setDebouncedSearch("");
                      setPage(1);
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <FiX size={14} />
                  </button>
                )}
              </div>

              {/* Type Filter Buttons */}
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Type:</span>
                <div className="inline-flex bg-gray-100 rounded-xl p-1 border border-gray-200 shadow-2xs text-xs font-semibold">
                  {TYPE_OPTIONS.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => {
                        setTypeFilter(t.key);
                        setPage(1);
                      }}
                      className={`px-3 py-1 rounded-lg transition ${
                        typeFilter === t.key
                          ? "bg-[#ffff00] text-black font-bold shadow-2xs"
                          : "text-gray-600 hover:text-gray-900"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Source Filters Bar */}
            <div className="flex items-center gap-1.5 flex-wrap pt-1 border-t border-gray-100">
              <span className="text-xs font-bold text-gray-400 uppercase tracking-wider mr-1">Source:</span>
              {SOURCE_TABS.map((tab) => {
                const isActive = sourceFilter === tab.key;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => {
                      setSourceFilter(tab.key);
                      setPage(1);
                    }}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition shadow-2xs ${
                      isActive
                        ? "bg-gray-900 text-[#ffff00] shadow-xs"
                        : "bg-white text-gray-600 hover:bg-gray-100 hover:text-gray-900 border border-gray-200"
                    }`}
                  >
                    <span>{tab.icon}</span>
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Ledger Table */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                <FiLayers size={16} className="text-gray-500" />
                <span>Transaction Ledger Records ({pagination.total} total)</span>
              </h3>
              <p className="text-xs text-gray-400">
                Page {pagination.page || page} of {pagination.totalPages || 1}
              </p>
            </div>

            {loading ? (
              <div className="py-24 flex flex-col items-center justify-center gap-3 text-gray-400">
                <div className="w-8 h-8 border-4 border-[#ffff00] border-t-transparent rounded-full animate-spin" />
                <p className="text-xs font-semibold text-gray-500">Loading coin transactions...</p>
              </div>
            ) : error ? (
              <div className="p-8 text-center bg-red-50 text-red-600 text-sm">
                <p className="font-bold">{error}</p>
                <button
                  onClick={() => fetchLedger(false)}
                  className="mt-3 px-4 py-1.5 text-xs font-bold bg-gray-900 text-white rounded-lg hover:bg-black transition"
                >
                  Retry
                </button>
              </div>
            ) : history.length === 0 ? (
              <div className="py-24 text-center text-gray-400 flex flex-col items-center justify-center gap-2">
                <div className="w-12 h-12 rounded-2xl bg-gray-100 text-gray-400 flex items-center justify-center text-2xl">
                  📂
                </div>
                <p className="text-sm font-semibold text-gray-700">No coin transactions found</p>
                <p className="text-xs text-gray-400">Try adjusting your filters or search query.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200/80 text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                      <th className="px-5 py-3.5">Date & Time</th>
                      <th className="px-5 py-3.5">User</th>
                      <th className="px-5 py-3.5">Source</th>
                      <th className="px-5 py-3.5">Type</th>
                      <th className="px-5 py-3.5 text-right">Amount</th>
                      <th className="px-5 py-3.5 text-right">Balance After</th>
                      <th className="px-5 py-3.5 text-center">Status</th>
                      <th className="px-5 py-3.5">Details / Note</th>
                      <th className="px-5 py-3.5 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {history.map((tx, idx) => {
                      const isCredit = (tx.transactionType || "").toUpperCase() === "CREDIT";
                      const badgeStyle = getSourceBadgeStyle(tx.source);
                      const sourceIcon = getSourceIcon(tx.source);
                      const isPoints = tx.source === "event_point" || tx.source === "event_points" || tx.source === "points" || (tx.unit || "").toUpperCase() === "PTS";
                      const currencyUnit = isPoints ? "PTS" : (tx.unit || "ZX");

                      return (
                        <tr
                          key={tx.id || `tx-${idx}`}
                          className="hover:bg-amber-50/20 transition-colors"
                        >
                          {/* Date */}
                          <td className="px-5 py-4 font-mono text-gray-600 whitespace-nowrap">
                            <div className="flex items-center gap-1.5">
                              <FiCalendar size={12} className="text-gray-400" />
                              <span>{formatDateTime(tx.transactionDate || tx.createdAt)}</span>
                            </div>
                          </td>

                          {/* User */}
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-lg bg-amber-100 text-amber-800 font-bold flex items-center justify-center text-xs flex-shrink-0">
                                {(tx.username || "U")[0].toUpperCase()}
                              </div>
                              <div className="min-w-0">
                                <div className="font-bold text-gray-800 truncate max-w-[140px]">
                                  {tx.username || `User #${tx.userId}`}
                                </div>
                                <div className="text-[10px] text-gray-400 font-mono">
                                  #{tx.userId} {tx.email ? `· ${tx.email}` : ""}
                                </div>
                              </div>
                            </div>
                          </td>

                          {/* Source */}
                          <td className="px-5 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold border ${badgeStyle}`}
                            >
                              <span>{sourceIcon}</span>
                              <span>{tx.sourceLabel || tx.source || "System"}</span>
                            </span>
                          </td>

                          {/* Type */}
                          <td className="px-5 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-black uppercase tracking-wide ${
                                isCredit
                                  ? "bg-emerald-100/80 text-emerald-800"
                                  : "bg-rose-100/80 text-rose-800"
                              }`}
                            >
                              {isCredit ? "+ CREDIT" : "- DEBIT"}
                            </span>
                          </td>

                          {/* Amount */}
                          <td className="px-5 py-4 text-right font-mono font-bold whitespace-nowrap">
                            <span
                              className={`text-sm ${
                                isCredit ? "text-emerald-600" : "text-rose-600"
                              }`}
                            >
                              {isCredit ? "+" : "-"}
                              {formatCoinAmount(tx.amount)} {currencyUnit}
                            </span>
                          </td>

                          {/* Balance After */}
                          <td className="px-5 py-4 text-right font-mono text-gray-700 whitespace-nowrap">
                            {tx.balanceAfter != null ? (
                              <span className="font-semibold">{formatCoinAmount(tx.balanceAfter)} {currencyUnit}</span>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>

                          {/* Status */}
                          <td className="px-5 py-4 text-center whitespace-nowrap">
                            <span className="inline-block px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-gray-100 text-gray-700 border border-gray-200">
                              {tx.status || "Completed"}
                            </span>
                          </td>

                          {/* Details */}
                          <td className="px-5 py-4 text-gray-600 max-w-xs">
                            {(() => {
                              const srcLower = String(tx.source || "").toLowerCase();
                              const detailsStr = String(tx.details || "");
                              const is2048 = srcLower.includes("2048") || detailsStr.includes("2048");
                              const isFruit = srcLower.includes("fruit") || detailsStr.toLowerCase().includes("fruit");

                              if (is2048) {
                                return (
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="inline-flex items-center gap-1 font-bold text-amber-800 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                                      <span>🧩</span>
                                      <span>{detailsStr || `2048 Game | Event: ${tx.eventTitle || "Zeta Rush"}`}</span>
                                    </span>
                                  </div>
                                );
                              }
                              if (isFruit) {
                                return (
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="inline-flex items-center gap-1 font-bold text-rose-800 bg-rose-50 px-2 py-0.5 rounded border border-rose-200">
                                      <span>🍉</span>
                                      <span>{detailsStr || `Fruit Bomb Game | Event: ${tx.eventTitle || "Zeta Rush"}`}</span>
                                    </span>
                                  </div>
                                );
                              }

                              return (
                                <span className="line-clamp-2" title={detailsStr || ""}>
                                  {detailsStr || "—"}
                                </span>
                              );
                            })()}
                          </td>

                          {/* Actions */}
                          <td className="px-5 py-4 text-center whitespace-nowrap">
                            <div className="flex items-center justify-center gap-1.5">
                              <button
                                onClick={() => navigate(`/user/${tx.userId}/coin-history`)}
                                className="p-1.5 rounded-lg text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 transition"
                                title="View User Coin History Page"
                              >
                                🪙
                              </button>
                              <button
                                onClick={() => navigate(`/user/${tx.userId}`)}
                                className="p-1.5 rounded-lg text-gray-600 hover:text-black hover:bg-gray-100 transition"
                                title="View User Profile"
                              >
                                <FiExternalLink size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination Controls */}
            {history.length > 0 && (
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500 flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <span>
                    Showing{" "}
                    <strong>
                      {history.length > 0 ? (page - 1) * perPage + 1 : 0}
                    </strong>{" "}
                    to{" "}
                    <strong>
                      {Math.min(page * perPage, pagination.total || history.length)}
                    </strong>{" "}
                    of <strong>{pagination.total || history.length}</strong> transactions
                  </span>
                  <div className="flex items-center gap-1.5 ml-2">
                    <span className="text-gray-400 font-medium">Per page:</span>
                    <select
                      value={perPage}
                      onChange={(e) => {
                        setPerPage(Number(e.target.value));
                        setPage(1);
                      }}
                      className="bg-white border border-gray-200 rounded-lg px-2 py-1 text-xs font-semibold text-gray-700 outline-hidden hover:border-gray-300 focus:border-yellow-400 cursor-pointer shadow-2xs"
                    >
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={200}>200</option>
                    </select>
                  </div>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed font-medium transition shadow-2xs"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1.5 text-xs font-bold text-gray-700 bg-white border border-gray-200 rounded-lg shadow-2xs">
                    Page {pagination.page || page} of {pagination.totalPages || 1}
                  </span>
                  <button
                    disabled={page >= (pagination.totalPages || 1) || loading}
                    onClick={() => setPage((p) => Math.min(pagination.totalPages || 1, p + 1))}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed font-medium transition shadow-2xs"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

